import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useNotify } from "react-admin";

import { callApi } from "@/dataprovider/misc-apis";

import { LeaveBalanceAlert } from "./LeaveBalanceAlert";
import { LeaveRangeBreakupAlert } from "./LeaveRangeBreakupAlert";
import {
  LEAVE_TYPE_CHOICES,
  leaveApiErrorMessage,
  transformMyLeaveCreatePayload,
  type EmployeeLeaveType,
} from "./leave-request-helpers";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

/**
 * Self-serve leave apply (POST employeeLeaveRequests/me).
 * Used on My dashboard; does not require employees.create.
 */
export function SelfLeaveCreateDialog({ open, onClose, onCreated }: Props) {
  const notify = useNotify();
  const [leaveType, setLeaveType] = useState<EmployeeLeaveType | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setLeaveType("");
    setStartDate("");
    setEndDate("");
    setReason("");
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const body = transformMyLeaveCreatePayload({
        leaveType: leaveType || undefined,
        startDate,
        endDate,
        reason,
      });
      await callApi({
        location: "employeeLeaveRequests/me",
        method: "POST",
        data: body,
      });
      notify("Leave request submitted", { type: "success" });
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      notify(leaveApiErrorMessage(err, "Failed to submit leave request"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Apply for leave</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <LeaveBalanceAlert mode="self" />
          <TextField
            select
            label="Leave type"
            value={leaveType}
            onChange={(e) => setLeaveType(e.target.value as EmployeeLeaveType)}
            fullWidth
            size="small"
            required
          >
            {LEAVE_TYPE_CHOICES.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Start date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            size="small"
            required
            InputLabelProps={{ shrink: true }}
          />
          <LeaveRangeBreakupAlert
            mode="self"
            leaveType={leaveType}
            startDate={startDate}
            endDate={endDate}
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={busy}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
